import { Injectable } from '../../injection';
import { isNil } from '../../utils';
import { IMobizStorageService } from './mobiz-storage.interface';

/**
 * @inheritdoc
 */
@Injectable()
export class MobizStorageService implements IMobizStorageService {
  /** The key used to store the list of registered key that needs to be cleared. */
  protected REGISTERED_KEYS_TO_CLEAR_KEY = 'mobiz-storage-registeredKeysToClear';

  /** The key used to store if the we need to use session storage or not. */
  protected USE_SESSION_STORAGE_KEY = 'useSessionStorage';

  /**
   * @inheritdoc
   */
  public get useSessionStorage(): boolean {
    let useSessionStorage = window.localStorage.getItem(this.USE_SESSION_STORAGE_KEY);
    return useSessionStorage === 'true';
  }

  /**
   * @inheritdoc
   */
  public set useSessionStorage(value: boolean) {
    window.localStorage.setItem(this.USE_SESSION_STORAGE_KEY, String(value));
  }

  /**
   * @inheritdoc
   */
  public set<T>(key: string, object: T, persistOnClear: boolean = false): void {
    let objectStr = JSON.stringify(object);
    this.storageArea.setItem(key, objectStr);

    if (!persistOnClear) {
      this.registerKeyForClear(key);
    } else {
      this.unregisterKeyForClear(key);
    }
  }

  /**
   * @inheritdoc
   */
  public get<T>(key: string): T | null {
    try {
      let objectStr: any | null = this.storageArea.getItem(key);
      if (objectStr) {
        return JSON.parse(objectStr);
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  /**
   * @inheritdoc
   */
  public remove(key: string): void {
    this.storageArea.removeItem(key);
  }

  /**
   * @inheritdoc
   */
  public registerKeyForClear(key: string): void {
    let registeredKeys = this.getKeysToClear();
    registeredKeys.add(key);
    this.persistKeysToClear(registeredKeys);
  }

  /**
   * @inheritdoc
   */
  public unregisterKeyForClear(key: string): void {
    let registeredKeys = this.getKeysToClear();
    registeredKeys.delete(key);
    this.persistKeysToClear(registeredKeys);
  }

  /**
   * @inheritdoc
   */
  public getKeysToClear(): Set<string> {
    if (isNil(localStorage, this.REGISTERED_KEYS_TO_CLEAR_KEY)) {
      this.persistKeysToClear(new Set([]));
    }

    return new Set<string>(JSON.parse(localStorage[this.REGISTERED_KEYS_TO_CLEAR_KEY]));
  }

  /**
   * @inheritdoc
   */
  public clearKey(key: string): void {
    this.clearKeys([key], this.storageArea);
  }

  /**
   * @inheritdoc
   */
  public clear(): void {
    let keysToRemove = Array.from(this.getKeysToClear());
    this.clearKeys(keysToRemove, this.storageArea);

    localStorage.removeItem(this.REGISTERED_KEYS_TO_CLEAR_KEY);
  }

  /**
   * Persist keys that are registered to be removed on storage clear.
   * Keys need to be persisted so they are not lost on page refresh.
   * @param keysToRemove The keys that will be removed on storage clear.
   */
  protected persistKeysToClear(keysToRemove: Set<string>): void {
    localStorage[this.REGISTERED_KEYS_TO_CLEAR_KEY] = JSON.stringify(Array.from(keysToRemove));
  }

  /**
   * Clears keys from target storage. This method is useful if we need more control in how keys should be cleared.
   * @param keysToRemove Keys to be removed.
   * @param targetStorage The target storage, eg: localStorage | sessionStorage.
   */
  protected clearKeys(keysToRemove: string[], targetStorage: Storage): void {
    keysToRemove.forEach((itemKey) => {
      targetStorage.removeItem(itemKey);
    });
  }

  private get storageArea(): Storage {
    if (this.useSessionStorage) {
      return window.sessionStorage;
    } else {
      return window.localStorage;
    }
  }
}
